/**
 * BaseTenTool.jsx — Cycle 55 · Teach & Explain
 * ─────────────────────────────────────────────────
 * Dienes / Base-Ten Blocks. Teachers add units, rods (10), flats (100),
 * cubes (1000). One-tap "regroup" merges 10 of a smaller unit into one
 * of the next larger unit — the core "why place value works" gesture.
 *
 * State is URL-serialized: ?u=6&r=3&f=1&c=0
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";
import { Plus, Minus, ArrowUpRight } from "lucide-react";

const clampInt = (v, lo, hi) => Math.max(lo, Math.min(hi, parseInt(v || 0, 10) || 0));

// Prop-driven display (also used by ManipulativeEmbed).
export function BaseTenDisplay({ counts, setCounts, readOnly = false }) {
  const { u, r, f, c } = counts;
  const total = u + r * 10 + f * 100 + c * 1000;

  const step = (k, d) => setCounts({ ...counts, [k]: Math.max(0, (counts[k] || 0) + d) });

  const regroup = (from, to) => {
    // Ten of `from` → one of `to`.
    const groups = Math.floor((counts[from] || 0) / 10);
    if (groups <= 0) return;
    setCounts({ ...counts, [from]: counts[from] - groups * 10, [to]: (counts[to] || 0) + groups });
  };

  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="bt-canvas">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bt-columns">
        <Column label="Cubes"    place="1000" tone="bg-fuchsia-100" k="c" count={c} step={step} readOnly={readOnly}>
          <div className="grid grid-cols-2 gap-0.5">
            {Array.from({ length: Math.min(c, 6) }).map((_, i) => (
              <div key={i} className="w-8 h-8 rounded-md border-2 border-slate-900 bg-fuchsia-400" />
            ))}
            {c > 6 && <span className="col-span-2 text-xs font-bold text-slate-600">+{c - 6} more</span>}
          </div>
        </Column>
        <Column label="Flats"    place="100"  tone="bg-emerald-100" k="f" count={f} step={step} readOnly={readOnly}>
          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: Math.min(f, 9) }).map((_, i) => (
              <div key={i} className="w-6 h-6 rounded border-2 border-slate-900 bg-emerald-400 grid grid-cols-2 grid-rows-2 gap-[1px]">
                <span className="bg-emerald-200" /><span className="bg-emerald-200" /><span className="bg-emerald-200" /><span className="bg-emerald-200" />
              </div>
            ))}
            {f > 9 && <span className="col-span-3 text-xs font-bold text-slate-600">+{f - 9} more</span>}
          </div>
        </Column>
        <Column label="Rods"     place="10"   tone="bg-sky-100" k="r" count={r} step={step} readOnly={readOnly}>
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: Math.min(r, 12) }).map((_, i) => (
              <div key={i} className="w-3 h-14 rounded-sm border-2 border-slate-900 bg-sky-400" />
            ))}
            {r > 12 && <span className="text-xs font-bold text-slate-600 self-end">+{r - 12}</span>}
          </div>
        </Column>
        <Column label="Units"    place="1"    tone="bg-amber-100" k="u" count={u} step={step} readOnly={readOnly}>
          <div className="grid grid-cols-4 gap-1">
            {Array.from({ length: Math.min(u, 20) }).map((_, i) => (
              <div key={i} className="w-3.5 h-3.5 rounded-sm border-2 border-slate-900 bg-amber-400" />
            ))}
            {u > 20 && <span className="col-span-4 text-xs font-bold text-slate-600">+{u - 20}</span>}
          </div>
        </Column>
      </div>

      {!readOnly && (
        <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="bt-regroup">
          <button type="button" onClick={() => regroup("u", "r")}
                  disabled={u < 10}
                  data-testid="bt-regroup-u-r"
                  className="inline-flex items-center gap-1 h-10 px-3 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40">
            <ArrowUpRight size={12} strokeWidth={3} /> 10 units → 1 rod
          </button>
          <button type="button" onClick={() => regroup("r", "f")} disabled={r < 10}
                  data-testid="bt-regroup-r-f"
                  className="inline-flex items-center gap-1 h-10 px-3 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40">
            <ArrowUpRight size={12} strokeWidth={3} /> 10 rods → 1 flat
          </button>
          <button type="button" onClick={() => regroup("f", "c")} disabled={f < 10}
                  data-testid="bt-regroup-f-c"
                  className="inline-flex items-center gap-1 h-10 px-3 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-xs shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40">
            <ArrowUpRight size={12} strokeWidth={3} /> 10 flats → 1 cube
          </button>
        </div>
      )}

      <p className="mt-4 text-center font-heading text-2xl md:text-3xl font-black tabular-nums text-slate-900" data-testid="bt-total">
        {c > 0 && `${c}×1000 · `}{f > 0 && `${f}×100 · `}{r > 0 && `${r}×10 · `}{u > 0 && `${u}×1 · `}
        <span className="ml-1 px-3 rounded-xl bg-slate-900 text-white">= {total}</span>
      </p>
    </div>
  );
}

function Column({ label, place, tone, k, count, step, readOnly, children }) {
  return (
    <div className={`rounded-2xl border-[3px] border-slate-900 ${tone} p-3`} data-testid={`bt-col-${k}`}>
      <div className="flex items-baseline justify-between">
        <p className="font-heading font-black text-sm text-slate-900">{label}</p>
        <p className="text-[10px] font-bold text-slate-500 tabular-nums">×{place}</p>
      </div>
      <div className="mt-2 min-h-[64px] bg-white/60 rounded-xl border-2 border-dashed border-slate-400 p-1.5 flex items-center justify-center">
        {count === 0 ? <span className="text-[10px] font-bold text-slate-400">empty</span> : children}
      </div>
      <div className="mt-2 flex items-center justify-between gap-1">
        <button type="button" onClick={() => step(k, -1)} disabled={readOnly || count === 0}
                data-testid={`bt-${k}-minus`}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-slate-900 bg-white font-black shadow-[0_2px_0_0_rgba(15,23,42,1)] disabled:opacity-40">
          <Minus size={12} strokeWidth={4} />
        </button>
        <p className="font-heading text-lg font-black tabular-nums" data-testid={`bt-${k}-count`}>{count}</p>
        <button type="button" onClick={() => step(k, +1)} disabled={readOnly}
                data-testid={`bt-${k}-plus`}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-slate-900 bg-white font-black shadow-[0_2px_0_0_rgba(15,23,42,1)] disabled:opacity-40">
          <Plus size={12} strokeWidth={4} />
        </button>
      </div>
    </div>
  );
}

export default function BaseTenTool() {
  const [params, setParams] = useSearchParams();
  const counts = {
    u: clampInt(params.get("u"), 0, 200),
    r: clampInt(params.get("r"), 0, 200),
    f: clampInt(params.get("f"), 0, 200),
    c: clampInt(params.get("c"), 0, 200),
  };
  const setCounts = (next) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (!v) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };
  return (
    <ManipulativeFrame
      slug="base-ten"
      title="Base-Ten Blocks"
      description="Add units, rods, flats and cubes. Tap regroup to see 10 small pieces become one bigger piece — the core place-value idea."
      onReset={() => setCounts({ u: 0, r: 0, f: 0, c: 0 })}
      shareUrl={window.location.href}
    >
      <BaseTenDisplay counts={counts} setCounts={setCounts} />
    </ManipulativeFrame>
  );
}
